import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LearnPage from './LearnPage';
import AlphabetTables from '../../components/AlphabetTables';
import { useCatalog } from '../../hooks/useCatalog';
import { useLangMap } from '../../hooks/useLanguages';
import { fetchSkillPathData } from '../../services/dataApi';
import { applyNativeOverlay, loadAlphabetCatalog } from '../../services/topicLessons';
import { useAuth } from '../../context/AuthContext';
import { loadBasicPhraseSections } from '../../services/basicPhrasesV2';

function useLearnData(homeLang, targetCode, nativeCode) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!homeLang || !targetCode || !nativeCode) return;
    let active = true;
    setLoading(true);
    setError(null);
    loadBasicPhraseSections(homeLang, targetCode, nativeCode)
      .then((sections) => {
        if (!active) return;
        setData(sections);
        setLoading(false);
      })
      .catch((e) => {
        if (!active) return;
        setError(e.message);
        setLoading(false);
      });
    return () => { active = false; };
  }, [homeLang, targetCode, nativeCode]);

  return { data, loading, error };
}

// alphabet/lesson1.json's top level optionally carries a table document —
// { id, type: "table", title, note, columns, rows } — feeding the static
// reference-table tab (not every language's lesson1.json has this; one
// whose table lives only inside the lesson's own section content just
// renders an empty table here, which degrades gracefully). Every text leaf
// is wrapped as { txt, translate, translationHint }; unwrap it and package
// as the one-element `sets` array AlphabetTables/filterForLanguagePair
// expect, rather than changing that component.
//
// native/{code}/lesson1.json overlays this same document — title/note/
// columns/rows here, and separately the lesson's own content — merged via
// the same sparse, positional applyNativeOverlay used everywhere else in
// this schema (see loadAlphabetLesson in services/topicLessons.js), so one
// overlay file covers both instead of needing a second one just for the
// table.
function unwrapAlphabetText(value) {
  return value && typeof value === 'object' && 'txt' in value ? value.txt : value;
}

function normalizeAlphabetTable(document) {
  const columns = (document.columns || []).map(unwrapAlphabetText);
  const rows = (document.rows || []).map((row) => row.map(unwrapAlphabetText));
  return {
    note: unwrapAlphabetText(document.note),
    sets: [{
      title: unwrapAlphabetText(document.title),
      columns,
      rows,
    }],
  };
}

function useAlphabetData(langCode, nativeCode, enabled) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!enabled || !langCode) {
      setData(null);
      return;
    }
    let active = true;
    setData(null);
    Promise.all([
      fetchSkillPathData(langCode, 'alphabet/lesson1.json'),
      nativeCode && nativeCode !== langCode
        ? fetchSkillPathData(langCode, `alphabet/native/${nativeCode}/lesson1.json`).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([document, overlay]) => {
        if (!active) return;
        const merged = overlay ? applyNativeOverlay(document, overlay) : document;
        setData(normalizeAlphabetTable(merged));
      })
      .catch(() => {
        if (active) setData(null);
      });
    return () => { active = false; };
  }, [langCode, nativeCode, enabled]);

  return data;
}

function useAlphabetChapters(langCode, label, enabled) {
  const [chapters, setChapters] = useState(null);

  useEffect(() => {
    if (!enabled || !langCode) {
      setChapters(null);
      return undefined;
    }
    let active = true;
    loadAlphabetCatalog(langCode, label)
      .then((loaded) => { if (active) setChapters(loaded); })
      .catch(() => { if (active) setChapters([]); });
    return () => { active = false; };
  }, [langCode, label, enabled]);

  return chapters;
}

export default function GenericLearnPage() {
  const { langCode } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const { catalog, loading: catLoading } = useCatalog();
  const { langMap } = useLangMap();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'alphabet' ? 'alphabet' : 'basics');

  const catalogEntry = catalog?.[langCode];
  const hasBasics   = catalogEntry?.features?.includes('basics');
  const hasAlphabet = catalogEntry?.features?.includes('alphabet');

  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const targetCode = langCode;
  // basic-phrases-sentences content resolves directly by locale code
  // (see basicPhrasesV2.js) — targetCode/nativeCode can be any of the ~40
  // supported languages independent of the curriculum; homeLang isn't used
  // for that lookup but is still threaded through for other calls that do
  // need it (e.g. useLearnData's other data sources).
  const homeLang = languagePrefs?.targetLanguage || 'en';
  const { data: learnData, loading: learnLoading, error: learnError } = useLearnData(homeLang, targetCode, nativeCode);
  const alphabetData = useAlphabetData(langCode, nativeCode, hasAlphabet);
  const alphabetChapters = useAlphabetChapters(langCode, langMap[targetCode]?.label, hasAlphabet);

  useEffect(() => {
    if (!catLoading && catalog && !hasAlphabet && activeTab === 'alphabet') {
      setActiveTab('basics');
    }
  }, [catLoading, catalog, hasAlphabet, activeTab]);

  if (!catLoading && catalog && !catalogEntry) {
    return <Navigate to="/" replace />;
  }

  if (!catLoading && catalog && !hasBasics) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page-shell">
      {hasAlphabet && (
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'basics' ? 'active' : ''}`}
            onClick={() => setActiveTab('basics')}
          >
            {t('learn.basicsTab')}
          </button>
          <button
            className={`tab-btn ${activeTab === 'alphabet' ? 'active' : ''}`}
            onClick={() => setActiveTab('alphabet')}
          >
            {t('learn.alphabetPronTab')}
          </button>
        </div>
      )}

      {activeTab === 'basics' && (
        learnLoading ? (
          <div className="page-panel">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-slate-100 rounded-xl" />
              ))}
            </div>
          </div>
        ) : learnError ? (
          <div className="page-panel">
            <p className="text-red-600">{t('learn.failed')}</p>
          </div>
        ) : learnData ? (
          <LearnPage
            title={langMap[targetCode]?.label || targetCode.toUpperCase()}
            sections={learnData}
            targetDir={langMap[targetCode]?.dir || 'ltr'}
            nativeDir={langMap[nativeCode]?.dir || 'ltr'}
            targetTts={langMap[targetCode]?.tts || targetCode}
          />
        ) : null
      )}

      {activeTab === 'alphabet' && (
        alphabetData ? (
          <AlphabetTables
            data={alphabetData}
            chapters={alphabetChapters}
            nativeCode={nativeCode}
            targetCode={targetCode}
            nativeLabel={langMap[nativeCode]?.label}
            targetLabel={langMap[targetCode]?.label}
            nativeDir={langMap[nativeCode]?.dir || 'ltr'}
            targetDir={langMap[targetCode]?.dir || 'ltr'}
            targetTts={langMap[targetCode]?.tts || targetCode}
          />
        ) : (
          <div className="page-panel">
            <div className="animate-pulse space-y-4">
              {[1, 2].map((i) => <div key={i} className="h-48 bg-slate-100 rounded-xl" />)}
            </div>
          </div>
        )
      )}
    </div>
  );
}
